import { ChevronDown, ChevronUp, MoreHorizontal, Plus, Trash2 } from "lucide-react";
import { Dispatch, memo, useCallback, useMemo, useState } from "react";
import { toast } from "sonner";

import { Button } from "@buildpy/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@buildpy/ui/dropdown-menu";
import { Input } from "@buildpy/ui/input";
import { SimpleSelect } from "@buildpy/ui/select";
import { cn } from "@/lib/utils";
import { safeCalcItem } from "@buildpy/core/modules/quote/safe-calc";
import { formatAmount } from "@/modules/quote/ui/format";
import Money from "@/components/money";
import {
  cardQtyNoteOf,
  commitCardMoney,
  commitCardQty,
  commitMarginText,
} from "@buildpy/core/modules/quote/editor/sheet/cell-edit";
import { marginPctText, profitRatePctText } from "@buildpy/core/modules/quote/editor/sheet/grid-model";
import { itemMarginOf, marginEditable } from "@buildpy/core/modules/quote/editor/reducer";
import type { VatMode } from "@buildpy/core/modules/money/vat";
import {
  EditorAction,
  EditorItem,
  EditorItemPatch,
  ItemRef,
} from "@buildpy/core/modules/quote/editor/types";
import SaleOverrideNote from "@/modules/quote/ui/editor/sale-override-note";
import UnitCell from "@/modules/quote/ui/editor/unit-cell";
import { dropsEditWhenOff, openUnlessLocked } from "@/modules/quote/ui/editor/locked-guard";

// 카드 칸 편집 한 번 — 유효한 글자는 키마다 반영하고, 무효로 떠나면 들어올 때 값(restore)으로 되돌린다.
export interface CardEdit {
  draft: string | null;
  error: string | null;
  restore: EditorItemPatch;
  committed: boolean;
}

export const cardEditFocus = (draft: string | null, restore: EditorItemPatch): CardEdit => ({
  draft,
  error: null,
  restore,
  committed: false,
});

export const cardEditChange = (edit: CardEdit, draft: string, error: string | null): CardEdit => ({
  ...edit,
  draft,
  error,
  committed: edit.committed || error === null,
});

// 되돌릴 칸이 지금 값과 전부 같은가 — 같으면 보내지 않아 dirty·되돌리기 단계가 안 생긴다. 식·원가·기준 마진은 undefined 와 null 을 같게 본다.
const sameAsNow = (restore: EditorItemPatch, now: EditorItem): boolean =>
  (Object.keys(restore) as (keyof EditorItemPatch)[]).every((k) =>
    k === "qty_expr" || k === "cost_unit_price" || k === "base_margin_bp"
      ? (restore[k] ?? null) === (now[k] ?? null)
      : restore[k] === now[k]
  );

export const cardEditBlur = (
  edit: CardEdit | null,
  now?: EditorItem
): { restore: EditorItemPatch | null; cancelled: { draft: string; message: string } | null } => {
  if (edit === null || edit.error === null) return { restore: null, cancelled: null };
  const restore = edit.committed && !(now && sameAsNow(edit.restore, now)) ? edit.restore : null;
  return { restore, cancelled: { draft: (edit.draft ?? "").trim(), message: edit.error } };
};

const toastCancelled = (id: string, cancelled: { draft: string; message: string } | null) => {
  if (cancelled === null) return;
  toast.error(`올바르지 않은 값이라 입력을 취소했습니다 (${cancelled.draft})`, {
    id,
    description: cancelled.message,
  });
};

const qtyRestoreOf = (item: EditorItem): EditorItemPatch => ({
  qty: item.qty,
  qty_expr: item.qty_expr ?? null,
  unit: item.unit,
});

// 수량칸이 마지막으로 본(들어올 때·보낸 뒤) 수량과 식.
interface QtySeen {
  qty: number;
  expr: string | null;
}

export interface QtyFieldState {
  edit: CardEdit | null;
  baseUnit: string;
  seen: QtySeen | null;
}

export const QTY_IDLE: QtyFieldState = { edit: null, baseUnit: "", seen: null };

const seenOf = (item: Pick<EditorItem, "qty" | "qty_expr">): QtySeen => ({ qty: item.qty, expr: item.qty_expr ?? null });

// 보낸 패치가 리듀서(applyItemPatch)를 지난 뒤의 수량·식 — 수량만 오면 식은 지워진다.
const seenAfter = (seen: QtySeen, patch: EditorItemPatch): QtySeen => ({
  qty: patch.qty ?? seen.qty,
  expr: "qty_expr" in patch ? patch.qty_expr || null : patch.qty !== undefined ? null : seen.expr,
});

// 지금 보일 입력 중 상태 — 되돌리기처럼 바깥에서 수량이 바뀌었으면 치던 글자를 버리고 지금 값을 보인다.
export const qtyLiveEdit = (s: QtyFieldState, item: Pick<EditorItem, "qty" | "qty_expr">): CardEdit | null =>
  s.seen !== null && (s.seen.qty !== item.qty || s.seen.expr !== (item.qty_expr ?? null)) ? null : s.edit;

export const qtyFocus = (item: EditorItem): QtyFieldState => ({
  edit: cardEditFocus(item.qty_expr ?? (item.qty === 0 ? "" : String(item.qty)), qtyRestoreOf(item)),
  baseUnit: item.unit,
  seen: seenOf(item),
});

// 글자 한 번 — 들어온 표시(포커스) 없이 오면 지금 단위를 기준으로 삼아, 수량만 고쳐도 단위가 지워지지 않게 한다.
export const qtyChange = (
  s: QtyFieldState,
  item: EditorItem,
  text: string,
  area: number | null,
  areaM2?: number | null
): { state: QtyFieldState; patch: EditorItemPatch | null } => {
  const live = qtyLiveEdit(s, item);
  const baseUnit = live === null ? item.unit : s.baseUnit;
  const seen = live === null || s.seen === null ? seenOf(item) : s.seen;
  const result = commitCardQty(item, text, { area_pyeong: area, area_m2: areaM2 }, baseUnit);
  const edit = cardEditChange(live ?? cardEditFocus(null, qtyRestoreOf(item)), text, result.ok ? null : result.message);
  return {
    patch: result.ok ? result.patch : null,
    state: { edit, baseUnit, seen: result.ok ? seenAfter(seen, result.patch) : seen },
  };
};

export const qtyBlur = (s: QtyFieldState, item: EditorItem) => cardEditBlur(qtyLiveEdit(s, item), item);

interface QtyFieldProps {
  item: EditorItem;
  area: number | null;
  areaM2?: number | null;
  disabled: boolean;
  onCommit: (patch: EditorItemPatch) => void;
  // 폰 편집 시트처럼 칸 모양을 바꿀 때만 — 테두리는 variant="outline", 높이·글씨는 className.
  variant?: "cell" | "outline";
  className?: string;
}

// 카드 보기 수량칸 — 시트와 같은 commitCell 로 숫자·식(`평*2.5`)을 받고, 못 읽는 글자로 떠나면 들어올 때 값으로 되돌린다.
export const QtyField = ({ item, area, areaM2, disabled, onCommit, variant = "cell", className }: QtyFieldProps) => {
  const [state, setState] = useState<QtyFieldState>(QTY_IDLE);
  if (dropsEditWhenOff(disabled, state !== QTY_IDLE)) setState(QTY_IDLE);
  const note = useMemo(() => cardQtyNoteOf(item, area, areaM2), [item, area, areaM2]);
  const edit = qtyLiveEdit(state, item);
  const error = edit?.error ?? null;

  return (
    <div className="flex min-w-0 flex-col">
      <Input
        aria-label="수량"
        className={cn("text-right tabular-nums", className)}
        disabled={disabled}
        errorMessage={error ?? undefined}
        title={error ?? undefined}
        variant={variant}
        value={edit?.draft ?? (item.qty === 0 ? "" : String(item.qty))}
        onBlur={() => {
          const out = qtyBlur(state, item);
          if (out.restore) onCommit(out.restore);
          toastCancelled("card-qty-commit", out.cancelled);
          setState(QTY_IDLE);
        }}
        onChange={(e) => {
          const next = qtyChange(state, item, e.target.value, area, areaM2);
          if (next.patch) onCommit(next.patch);
          setState(next.state);
        }}
        onFocus={(e) => {
          const next = qtyFocus(item);
          setState(next);
          e.currentTarget.value = next.edit?.draft ?? "";
          e.currentTarget.select();
        }}
      />
      {!disabled && note.kind !== "none" && (
        <span
          className={cn(
            // 폰 시트(320px 두 칸)는 좁아 식 안내를 자르지 않고 줄을 바꾸며 12px — md 이상 카드 표는 그대로 11px 한 줄.
            "truncate px-1.5 text-right text-2xs leading-tight phone:whitespace-normal phone:break-all phone:text-xs",
            note.kind === "error" ? "text-destructive" : "text-muted-foreground"
          )}
          title={note.kind === "error" ? note.message : `식 ${note.expr}`}>
          식 {note.expr}
          {note.kind === "expr" && note.waiting && " · 면적 입력 시 계산"}
          {note.kind === "error" && ` · ${note.message}`}
        </span>
      )}
    </div>
  );
};

interface MoneyFieldProps {
  ariaLabel: string;
  // 지금 행 — 되돌릴 값이 이미 같은지 본다.
  item: EditorItem;
  value: number;
  disabled: boolean;
  onCommit: (value: number) => void;
  // 무효로 떠날 때 되돌릴 칸들 — 간단 모드는 자재·노무 나눔까지, 제안 표시도 담는다.
  restore: EditorItemPatch;
  onRestore: (patch: EditorItemPatch) => void;
  // 폰 편집 시트처럼 칸 모양을 바꿀 때만 — 테두리는 variant="outline", 높이·글씨는 className.
  variant?: "cell" | "outline";
  className?: string;
}

// 카드 보기 단가칸 — 시트와 같은 상한·검증(commitCardMoney)을 쓰고, 못 읽는 글자로 떠나면 들어올 때 값으로 되돌린다.
export const MoneyField = ({ ariaLabel, item, value, disabled, onCommit, restore, onRestore, variant = "cell", className }: MoneyFieldProps) => {
  const [edit, setEdit] = useState<CardEdit | null>(null);
  if (dropsEditWhenOff(disabled, edit !== null)) setEdit(null);
  const error = edit?.error ?? null;

  return (
    <Input
      aria-label={ariaLabel}
      className={cn("text-right tabular-nums", className)}
      disabled={disabled}
      errorMessage={error ?? undefined}
      inputMode="numeric"
      title={error ?? undefined}
      variant={variant}
      value={edit?.draft ?? (value === 0 ? "" : formatAmount(value))}
      onBlur={() => {
        const out = cardEditBlur(edit, item);
        if (out.restore) onRestore(out.restore);
        toastCancelled("card-money-commit", out.cancelled);
        setEdit(null);
      }}
      onChange={(e) => {
        const text = e.target.value;
        const result = commitCardMoney(text);
        if (result.ok) onCommit(result.value);
        setEdit(cardEditChange(edit ?? cardEditFocus(null, restore), text, result.ok ? null : result.message));
      }}
      onFocus={(e) => {
        setEdit(cardEditFocus(null, restore));
        e.currentTarget.select();
      }}
    />
  );
};

interface CostFieldProps {
  item: EditorItem;
  disabled: boolean;
  // 잠긴 견적 — 값은 보이고 칸만 끈다(disabled 는 묶음 줄이라 값도 비운다).
  locked?: boolean;
  onCommit: (value: number | null) => void;
  onRestore: (patch: EditorItemPatch) => void;
  variant?: "cell" | "outline";
  className?: string;
}

// 원가칸 — 빈 칸은 「원가 없음」(null) · 숫자는 단가와 같은 검증 · 못 읽는 글자로 떠나면 들어올 때 값으로.
export const CostField = ({ item, disabled, locked = false, onCommit, onRestore, variant = "cell", className }: CostFieldProps) => {
  const [edit, setEdit] = useState<CardEdit | null>(null);
  if (dropsEditWhenOff(disabled || locked, edit !== null)) setEdit(null);
  const error = edit?.error ?? null;
  const value = disabled ? null : (item.cost_unit_price ?? null);
  const restore: EditorItemPatch = { cost_unit_price: item.cost_unit_price ?? null };

  return (
    <Input
      aria-label="원가"
      className={cn("text-right tabular-nums", className)}
      disabled={disabled || locked}
      errorMessage={error ?? undefined}
      inputMode="numeric"
      title={error ?? undefined}
      variant={variant}
      value={edit?.draft ?? (value === null ? "" : formatAmount(value))}
      onBlur={() => {
        const out = cardEditBlur(edit, item);
        if (out.restore) onRestore(out.restore);
        toastCancelled("card-cost-commit", out.cancelled);
        setEdit(null);
      }}
      onChange={(e) => {
        const text = e.target.value;
        const result = text.trim() === "" ? ({ ok: true, value: null } as const) : commitCardMoney(text);
        if (result.ok) onCommit(result.value);
        setEdit(cardEditChange(edit ?? cardEditFocus(null, restore), text, result.ok ? null : result.message));
      }}
      onFocus={(e) => {
        setEdit(cardEditFocus(null, restore));
        e.currentTarget.select();
      }}
    />
  );
};

interface MarginFieldProps {
  item: EditorItem;
  vatMode: VatMode;
  onCommit: (margin: number) => void;
  onRestore: (patch: EditorItemPatch) => void;
  // 잠긴 견적 — 고칠 수 있는 줄이어도 끈다.
  disabled?: boolean;
  // 판매가를 직접 고친 줄에 「직접 입력 · 되돌리기」 를 띄운다(없으면 안 띄움).
  onReset?: () => void;
  // 표 칸처럼 좁은 자리 — 안내를 줄여 쓴다.
  compactNote?: boolean;
  variant?: "cell" | "outline";
  className?: string;
}

// 마진칸(원가 대비 %) — 고치면 기준 마진이 바뀌고 판매 단가가 다시 계산된다 · 원가가 없거나 묶음 줄은 읽기만 · 아래에 이익률.
export const MarginField = ({ item, vatMode, disabled = false, onCommit, onRestore, onReset, compactNote = true, variant = "cell", className }: MarginFieldProps) => {
  const [edit, setEdit] = useState<CardEdit | null>(null);
  const margin = itemMarginOf(item, vatMode);
  const editable = marginEditable(item);
  if (dropsEditWhenOff(!editable || disabled, edit !== null)) setEdit(null);
  const error = edit?.error ?? null;
  const shown = margin === null ? "" : marginPctText(margin);
  const rate = margin === null ? "" : profitRatePctText(margin);
  const restore: EditorItemPatch = {
    material_unit_price: item.material_unit_price,
    labor_unit_price: item.labor_unit_price,
    base_margin_bp: item.base_margin_bp ?? null,
    suggested: item.suggested,
  };

  return (
    <div className="flex min-w-0 flex-col">
      <Input
        aria-label="마진"
        className={cn("text-right tabular-nums", className)}
        disabled={!editable || disabled}
        errorMessage={error ?? undefined}
        inputMode="decimal"
        placeholder="—"
        title={error ?? (editable ? undefined : "원가를 넣은 줄에서 고칠 수 있습니다")}
        variant={variant}
        value={edit?.draft ?? shown}
        onBlur={() => {
          const out = cardEditBlur(edit, item);
          if (out.restore) onRestore(out.restore);
          toastCancelled("card-margin-commit", out.cancelled);
          setEdit(null);
        }}
        onChange={(e) => {
          const text = e.target.value;
          const result = commitMarginText(text);
          if (result.ok) onCommit(result.margin);
          setEdit(cardEditChange(edit ?? cardEditFocus(null, restore), text, result.ok ? null : result.message));
        }}
        onFocus={(e) => {
          setEdit(cardEditFocus(null, restore));
          e.currentTarget.select();
        }}
      />
      {rate !== "" && (
        <span className="truncate px-1.5 text-right text-xs leading-tight text-muted-foreground">
          = 이익률 {rate}%
        </span>
      )}
      {onReset && <SaleOverrideNote compact={compactNote} item={item} vatMode={vatMode} onReset={onReset} />}
    </div>
  );
};

interface Props {
  item: EditorItem;
  /** 수량식의 「평」 값(견적의 평수). */
  area: number | null;
  /** 수량식의 「m²」 값 — 없으면 평×3.305785. */
  areaM2?: number | null;
  tradeKey: string;
  parentKey: string | null;
  dispatch: Dispatch<EditorAction>;
  /** 작업내역·규격을 다 넣고 빠져나왔을 때 단가표에 물어본다. */
  onSuggest: (ref: ItemRef) => void;
  /**
   * 공간 선택지(`codes.space`). 이게 있어야 「욕실만 얼마」 를 뽑을 수 있다 —
   * 부위(`scope`)는 「벽·천장」 이라 공간으로 묶이지 않는다.
   */
  spaces: { code: string; label: string }[];
  /**
   * 간단 모드인가. 자재·노무를 나눠 쓰는 회사는 소수라, 기본은 **합친 단가 하나**만
   * 받고 나눠야 하는 사람만 상세로 넘어간다.
   */
  simple: boolean;
  // 원가·마진 두 칸을 합계 뒤에 보일지 · 마진은 vatMode 기준.
  showCost?: boolean;
  vatMode?: VatMode;
  // 잠긴 견적 — 포인터다운으로 여는 공간·단위·행 메뉴를 끈다(나머지 칸은 편집기의 fieldset 이 끈다).
  disabled?: boolean;
}

/**
 * 항목 한 줄. 금액 세 칸은 입력이 아니라 계산 결과다 — 서버와 같은 calcItem 을 쓴다.
 * SET 부모는 자기 단가를 못 쓰므로 단가 칸을 잠근다.
 */
const ItemRow = ({
  item,
  tradeKey,
  parentKey,
  dispatch,
  onSuggest,
  spaces,
  simple,
  area,
  areaM2,
  showCost = false,
  vatMode = "INCL",
  disabled = false,
}: Props) => {
  // 행 메뉴 — 잠기면 열려 있어도 닫는다(포털이라 fieldset 이 못 끈다).
  const [menuOpen, setMenuOpen] = useState(false);
  const ref = useMemo<ItemRef>(
    () => ({ tradeKey, itemKey: item.key, parentKey }),
    [tradeKey, item.key, parentKey]
  );

  const isSet = item.children.length > 0;
  const isChild = parentKey !== null;
  // **던지지 않는 쪽을 쓴다.** 행마다 렌더 중에 세므로, 여기서 던지면 편집기가
  // 통째로 언마운트되어 미저장 편집이 사라진다.
  const amounts = useMemo(() => safeCalcItem(item), [item]);

  const patch = useCallback(
    (next: EditorItemPatch) => dispatch({ type: "SET_ITEM", ref, patch: next }),
    [dispatch, ref]
  );

  const settle = useCallback(() => onSuggest(ref), [onSuggest, ref]);

  const spaceOptions = useMemo(
    () => [{ value: "", label: <span className="text-muted-foreground">—</span> }, ...spaces.map((space) => ({ value: space.code, label: space.label }))],
    [spaces]
  );

  return (
    <tr className="border-b border-border last:border-0 hover:bg-muted/40">
      <td className="px-1 py-0.5">
        <SimpleSelect
          aria-label="공간"
          arrowKeysChangeValue
          className="h-7 min-h-7"
          disabled={disabled}
          options={spaceOptions}
          size="sm"
          variant="ghost"
          value={item.space_code}
          onValueChange={(next) =>
            dispatch({
              type: "SET_ITEM",
              ref,
              patch: { space_code: next },
            })
          }
        />
      </td>

      {!simple && (
      <td className={cn("px-1 py-0.5", isChild && "pl-4")}>
        <Input
          aria-label="범위"
          disabled={disabled}
            value={item.scope}
          variant="cell"
          onChange={(e) => patch({ scope: e.target.value })}
        />
      </td>
      )}

      <td className="px-1 py-0.5">
        <div className={cn("flex items-center gap-x-1", isChild && "pl-4")}>
          {isChild && <span className="text-xs text-muted-foreground">└</span>}
          <Input
            aria-label="작업내역"
            disabled={disabled}
                placeholder="작업내역"
            variant="cell"
            value={item.item}
            onBlur={settle}
            onChange={(e) => patch({ item: e.target.value })}
          />
          {isSet && (
            <span className="whitespace-nowrap rounded-md bg-muted px-1 text-xs text-muted-foreground">
              SET
            </span>
          )}
        </div>
      </td>

      <td className="px-1 py-0.5">
        <UnitCell
          disabled={isSet || disabled}
          value={item.unit}
          onChange={(unit) => patch({ unit })}
          onSettled={settle}
        />
      </td>

      <td className="px-1 py-0.5">
        <QtyField area={area} areaM2={areaM2} disabled={isSet || disabled} item={item} onCommit={patch} />
      </td>

      {simple ? (
        <td className="px-1 py-0.5">
          <div className="flex items-center gap-x-1">
            <MoneyField
              ariaLabel="단가"
              disabled={isSet || disabled}
              item={item}
              value={item.material_unit_price + item.labor_unit_price}
              restore={{
                material_unit_price: item.material_unit_price,
                labor_unit_price: item.labor_unit_price,
                suggested: item.suggested,
              }}
              onCommit={(value) =>
                dispatch({ type: "SET_SIMPLE_PRICE", ref, value })
              }
              onRestore={patch}
            />
            {item.suggested && (
              <span className="whitespace-nowrap rounded-md bg-status-new-bg px-1 text-xs text-status-new">
                제안값
              </span>
            )}
          </div>
        </td>
      ) : (
        <>
          <td className="px-1 py-0.5">
            <div className="flex items-center gap-x-1">
              <MoneyField
                ariaLabel="자재 단가"
                disabled={isSet || disabled}
                item={item}
                value={item.material_unit_price}
                restore={{ material_unit_price: item.material_unit_price, suggested: item.suggested }}
                onCommit={(material_unit_price) =>
                  patch({ material_unit_price })
                }
                onRestore={patch}
              />
              {item.suggested && (
                <span className="whitespace-nowrap rounded-md bg-status-new-bg px-1 text-xs text-status-new">
                  제안값
                </span>
              )}
            </div>
          </td>

          <td className="px-2 py-0.5 text-right text-sm text-muted-foreground">
            <Money value={amounts.material_amount} />
          </td>

          <td className="px-1 py-0.5">
            <MoneyField
              ariaLabel="노무 단가"
              disabled={isSet || disabled}
              item={item}
              value={item.labor_unit_price}
              restore={{ labor_unit_price: item.labor_unit_price, suggested: item.suggested }}
              onCommit={(labor_unit_price) => patch({ labor_unit_price })}
              onRestore={patch}
            />
          </td>

          <td className="px-2 py-0.5 text-right text-sm text-muted-foreground">
            <Money value={amounts.labor_amount} />
          </td>
        </>
      )}

      <td className="px-2 py-0.5 text-right text-sm font-medium">
        <Money value={amounts.amount} />
      </td>

      {showCost && (
        <>
          <td className="px-1 py-0.5">
            <CostField
              disabled={isSet}
              locked={disabled}
              item={item}
              onCommit={(value) => dispatch({ type: "SET_COST", ref, value })}
              onRestore={patch}
            />
          </td>
          <td className="px-1 py-0.5">
            <MarginField
              disabled={disabled}
              item={item}
              vatMode={vatMode}
              onCommit={(margin) => dispatch({ type: "SET_MARGIN", ref, margin })}
              onReset={() => dispatch({ type: "RESET_TO_CALC", ref })}
              onRestore={patch}
            />
          </td>
        </>
      )}

      <td className="px-1 py-0.5">
        <Input
          aria-label="비고"
          disabled={disabled}
            value={item.note}
          variant="cell"
          onChange={(e) => patch({ note: e.target.value })}
        />
      </td>

      <td className="px-1 py-0.5">
        <DropdownMenu open={openUnlessLocked(menuOpen, disabled)} onOpenChange={setMenuOpen}>
          <DropdownMenuTrigger asChild disabled={disabled}>
            <Button
              aria-label="행 메뉴"
              size="icon-xs"
              type="button"
              variant="ghost-muted">
              <MoreHorizontal className="size-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem
              onSelect={() => dispatch({ type: "MOVE_ITEM", ref, dir: -1 })}>
              <ChevronUp className="size-4" /> 위로
            </DropdownMenuItem>
            <DropdownMenuItem
              onSelect={() => dispatch({ type: "MOVE_ITEM", ref, dir: 1 })}>
              <ChevronDown className="size-4" /> 아래로
            </DropdownMenuItem>
            {!isChild && (
              <DropdownMenuItem
                onSelect={() =>
                  dispatch({ type: "ADD_CHILD", tradeKey, parentKey: item.key })
                }>
                <Plus className="size-4" /> SET 자식 추가
              </DropdownMenuItem>
            )}
            <DropdownMenuItem
              variant="destructive"
              onSelect={() => dispatch({ type: "REMOVE_ITEM", ref })}>
              <Trash2 className="size-4" /> 삭제
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </td>
    </tr>
  );
};

export default memo(ItemRow);
